/**
 * Component: GalenSearchPage
 * SpecReferenceID: SEARCH-HYBRID-V1
 * Requirements: SEARCH-HYBRID-V1-R02, SEARCH-HYBRID-V1-R04, SEARCH-HYBRID-V1-R05,
 *               SEARCH-HYBRID-V1-R06, SEARCH-HYBRID-V1-R07, SEARCH-HYBRID-V1-R09,
 *               SEARCH-HYBRID-V1-R10, FRONTEND-WEB-V1-R02, FRONTEND-WEB-V1-R13
 *
 * Galen Search interface combining LLM response with search results.
 * Displays AI-generated narrative above browsable search result cards.
 *
 * SEARCH-HYBRID-V1-R04: Render narrative AI response above the result list.
 * SEARCH-HYBRID-V1-R05: Display each result as a card with title, excerpt, metadata.
 * SEARCH-HYBRID-V1-R06: Keep search results user-browsable (not collapsed).
 * SEARCH-HYBRID-V1-R07: No multi-turn conversation history.
 * SEARCH-HYBRID-V1-R09: Track usage metrics (link opens, refinements).
 * FRONTEND-WEB-V1-R02: Collect user input via text or optional audio.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Search,
  Loader2,
  AlertCircle,
  Bot,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';
import { trackUsageAction } from '@/lib/usage-tracking';
import { useBranding, useIsFeatureEnabled } from '@/contexts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { UserInputArea } from '@/components/input/UserInputArea';
import { MarkdownRenderer } from '@/features/chat/components/MarkdownRenderer';
import {
  SearchFilterSidebar,
  SearchFilterMobileButton,
  INITIAL_FILTERS,
  type SearchFilters,
  type Facet,
} from './components/SearchFilterSidebar';
import { toFilterRequest, hasActiveFilters } from './components/search-filter-types';
import { useFilterAutoSearch } from './useFilterAutoSearch';
import { SearchPagination } from './components/SearchPagination';
import { SearchResultCard } from './components/SearchResultCard';
import { ResultDetailPanel } from './components/ResultDetailPanel';
import { normalizeQuotes } from './components/search-result-utils';

const DEFAULT_PAGE_SIZE = 20;

// API Types
interface GalenSearchResult {
  document_id: string;
  title: string;
  content: string;
  score: number;
  ranking_position: number;
  semantic_score: number | null;
  keyword_score: number | null;
  captions: string[];
  highlights: Record<string, string[]>;
  metadata: Record<string, unknown>;
  source_url: string | null;
}

interface GalenSearchResponse {
  llm_response: string | null;
  has_answer: boolean;
  is_grounded: boolean;
  results: GalenSearchResult[];
  total_count: number | null;
  facets: Facet[];
  query: string;
  mode: string;
  safety_decision: 'allow' | 'block' | 'clarify';
  safety_message: string | null;
}

type SearchMode = 'auto' | 'hybrid' | 'semantic';


/**
 * GalenSearchPage Component
 *
 * SEARCH-HYBRID-V1-R02: Display LLM response and search results together.
 * SEARCH-HYBRID-V1-R04: Render AI response above result list.
 * SEARCH-HYBRID-V1-R06: Keep results browsable (not collapsed).
 */
export function GalenSearchPage(): React.ReactElement {
  // Bug #3979: Read search mode from admin config instead of local state
  const { branding } = useBranding();
  const mode: SearchMode = (branding.galenSearchDefaultMode as SearchMode) || 'hybrid';
  // SPEECH-V1-R02 / Bug #4340: hide the mic affordance entirely when the
  // speech module is not enabled for this deployment.
  const isSpeechEnabled = useIsFeatureEnabled('speech');

  // State
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState<GalenSearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const previousQueryRef = useRef<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  // SEARCH-HYBRID-V1-R10: server-side faceted filtering state
  const [filters, setFilters] = useState<SearchFilters>(INITIAL_FILTERS);
  const resultsTopRef = useRef<HTMLDivElement>(null);
  // Bug #3878: State for detail panel
  const [detailSectionId, setDetailSectionId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  /**
   * Execute Galen Search request with pagination.
   * SEARCH-HYBRID-V1-R02: Execute combined search + LLM.
   * SEARCH-HYBRID-V1-R07: Each search is independent (no history).
   * SEARCH-SEMANTIC-V1-R03: Paginated result presentation.
   */
  const handleSearch = useCallback(
    async (e?: React.FormEvent, page = 1) => {
      e?.preventDefault();

      if (!query.trim()) {
        return;
      }

      // Track query refinement if applicable (R09)
      if (previousQueryRef.current && previousQueryRef.current !== query.trim()) {
        void trackUsageAction('query_refinement', {
          query_text: query.trim(),
          previous_query: previousQueryRef.current,
        });
      }

      setIsLoading(true);
      setError(null);
      setHasSearched(true);

      try {
        const skip = (page - 1) * DEFAULT_PAGE_SIZE;
        // SEARCH-HYBRID-V1-R10/R11: send refine selections for server-side
        // filtering + recency, so the answer and result list reflect the same
        // filtered set.
        const searchResponse = await api.post<GalenSearchResponse>('/search/galen', {
          query: normalizeQuotes(query.trim()),
          mode,
          include_captions: true,
          top_n: DEFAULT_PAGE_SIZE,
          skip,
          ...toFilterRequest(filters),
          prioritize_recent: filters.prioritizeRecent,
        });

        setResponse(searchResponse.data);
        setCurrentPage(page);
        previousQueryRef.current = query.trim();
      } catch (err) {
        setError(getErrorMessage(err));
        setResponse(null);
      } finally {
        setIsLoading(false);
      }
    },
    [query, mode, filters]
  );

  // SEARCH-HYBRID-V1-R10 (Bug #5052): server-side filtering re-runs the search
  // (debounced) when a refine selection changes, once a search has been run.
  useFilterAutoSearch(filters, hasSearched, () => void handleSearch(undefined, 1));

  const handlePageChange = useCallback(
    (page: number) => {
      void handleSearch(undefined, page);
      resultsTopRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
    [handleSearch]
  );

  /**
   * Handle link open: open detail panel for the clicked result.
   * Bug #3878: Now opens detail panel with section content instead of only tracking.
   * COUNTER tracking is handled by SearchResultCard internally.
   */
  const handleLinkOpen = useCallback(
    (_documentId: string, _rankingPosition: number, guidelineSectionId: number | null) => {
      if (guidelineSectionId != null) {
        setDetailSectionId(guidelineSectionId);
        setDetailOpen(true);
      }
    },
    []
  );

  // SEARCH-HYBRID-V1-R10 (Bug #5053): keep the refine sidebar visible when a
  // search has run and there are results OR active filters, so a zero-result
  // filter can still be cleared.
  const showFilters =
    hasSearched && ((response?.results.length ?? 0) > 0 || hasActiveFilters(filters));

  return (
    <div className="mx-auto max-w-4xl">
      {/* Page Header */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Galen Search</h1>
        <p className="mt-2 text-muted-foreground">
          Ask questions about clinical guidelines and get AI-powered answers with
          supporting evidence. You can type or use voice input.
        </p>
      </header>

      {/* Search Form - FRONTEND-WEB-V1-R02: Text and optional audio input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSearch();
        }}
        className="mb-8 space-y-4"
        role="search"
        aria-label="Galen Search"
      >
        {/* FRONTEND-WEB-V1-R13: Responsive layout - stack on mobile, row on desktop */}
        <div className="flex flex-col gap-4 md:flex-row">
          {/* FRONTEND-WEB-V1-R02: UserInputArea with audio support */}
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10"
              aria-hidden="true"
            />
            <div className="pl-8">
              <UserInputArea
                value={query}
                onChange={setQuery}
                onSubmit={() => void handleSearch()}
                placeholder="Ask a guideline related question..."
                disabled={isLoading}
                enableAudio={isSpeechEnabled}
                multiline={false}
                ariaLabel="Search query"
              />
            </div>
          </div>
          {/* Bug #3979: Search mode selector removed — mode is now read from admin config */}
          <Button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="min-h-touch"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </Button>
        </div>
      </form>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6" role="alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Safety Block/Clarify Message */}
      {response?.safety_decision === 'block' && (
        <Alert variant="destructive" className="mb-6" role="alert">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Query Blocked</AlertTitle>
          <AlertDescription>
            {response.safety_message ||
              'This query cannot be processed. Please try a different question.'}
          </AlertDescription>
        </Alert>
      )}

      {response?.safety_decision === 'clarify' && (
        <Alert className="mb-6" role="status">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Please Clarify</AlertTitle>
          <AlertDescription>
            {response.safety_message ||
              'Please rephrase your query to be more specific about guideline content.'}
          </AlertDescription>
        </Alert>
      )}

      {/* AI Response Section - SEARCH-HYBRID-V1-R04: Above results */}
      {/* SEARCH-HYBRID-V1-R02: Hide when backend signals no meaningful answer */}
      {response?.llm_response && response.has_answer && (
        <AIResponseSection
          response={response.llm_response}
          citationCount={response.results.length}
        />
      )}

      {/* SEARCH-HYBRID-V1-R10: Mobile filter button */}
      {showFilters && (
        <div className="md:hidden">
          <SearchFilterMobileButton
            filters={filters}
            onFiltersChange={setFilters}
            onReset={() => setFilters(INITIAL_FILTERS)}
            facets={response?.facets ?? []}
            resultCount={response?.results.length ?? 0}
          />
        </div>
      )}

      {/* Results area: sidebar on desktop + results */}
      <div
        ref={resultsTopRef}
        className={showFilters ? 'md:flex md:gap-6' : ''}
      >
        {/* SEARCH-HYBRID-V1-R10: Desktop filter sidebar */}
        {showFilters && (
          <aside className="hidden md:block w-56 shrink-0" aria-label="Search filters">
            <div className="sticky top-4">
              <SearchFilterSidebar
                filters={filters}
                onFiltersChange={setFilters}
                onReset={() => setFilters(INITIAL_FILTERS)}
                facets={response?.facets ?? []}
                resultCount={response?.results.length ?? 0}
              />
            </div>
          </aside>
        )}

        <div className="min-w-0 flex-1">
          {/* Results Summary */}
          {hasSearched && !isLoading && !error && response && (
            <div className="mb-4 text-sm text-muted-foreground" aria-live="polite">
              {response.results.length > 0 ? (
                <span>Showing {response.results.length} result{response.results.length !== 1 ? 's' : ''}</span>
              ) : (
                <span>No results found for &ldquo;{query}&rdquo;</span>
              )}
            </div>
          )}

          {/* Search Results - SEARCH-HYBRID-V1-R05 & R06: Cards, browsable */}
          <section
            aria-label="Search results"
            className="space-y-4"
            aria-busy={isLoading}
          >
            {response?.results.map((result) => (
              <SearchResultCard
                key={result.document_id}
                result={result}
                rank={result.ranking_position}
                onLinkOpen={handleLinkOpen}
                query={query}
                searchMode="galen"
              />
            ))}
          </section>

          {/* Pagination: Only rendered when total_count is available.
              Galen Search suppresses total_count because hybrid similarity
              scoring makes it misleading (Bug #3862). */}
          {response?.total_count != null && response.total_count > 0 && (
            <SearchPagination
              currentPage={currentPage}
              totalCount={response.total_count}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={handlePageChange}
              isLoading={isLoading}
            />
          )}

          {/* Empty State */}
          {hasSearched &&
            !isLoading &&
            response?.results.length === 0 &&
            !error &&
            response?.safety_decision !== 'block' && (
              <div className="py-12 text-center text-muted-foreground">
                <Search className="mx-auto mb-4 h-12 w-12 opacity-50" aria-hidden="true" />
                <p className="text-lg">No results found</p>
                <p className="mt-2">
                  Try adjusting your search terms or using different keywords.
                </p>
              </div>
            )}
        </div>
      </div>

      {/* Initial State */}
      {!hasSearched && (
        <div className="py-12 text-center text-muted-foreground">
          <Bot className="mx-auto mb-4 h-12 w-12 opacity-50" aria-hidden="true" />
          <p className="text-lg">Ask a question about clinical guidelines</p>
          <p className="mt-2">
            Galen will search for relevant content and provide an AI-generated
            summary along with supporting evidence.
          </p>
        </div>
      )}

      {/* Bug #3878: Detail panel for View Full Result */}
      <ResultDetailPanel
        guidelineSectionId={detailSectionId}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
}

/**
 * AIResponseSection Component
 *
 * SEARCH-HYBRID-V1-R04: Render narrative AI response above the result list.
 * Bug #3871: Uses MarkdownRenderer for proper markdown and [docN] citation badges.
 *            Copy icon removed from header per bug requirements.
 */
interface AIResponseSectionProps {
  response: string;
  citationCount: number;
}

function AIResponseSection({
  response,
  citationCount,
}: AIResponseSectionProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card className="mb-6 border-primary/20 bg-primary/5">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" aria-hidden="true" />
            <CardTitle className="text-lg">AI Response</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse response' : 'Expand response'}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          <MarkdownRenderer content={response} citationCount={citationCount} />
        </CardContent>
      )}
    </Card>
  );
}

export default GalenSearchPage;
